import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { FileText, Calendar, User, Building2, Eye } from 'lucide-react'

import {
    DataTableColumn,
    DataTableAction,
    FilterItemConfig,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import { Badge } from '@/components/ui/badge'
import AssetStatusBadge from '@/modules/Asset/components/AssetStatusBadge'
import { AssetListItem } from '@/modules/Asset/services'
import { getAssetDetailPath } from '@/modules/Asset/utils/navigation'
import { generatePathDatacentral, paths } from '@/utils/path'
import { DOCUMENT_GROUPS, DOCUMENT_TYPES } from '@/modules/Asset/constants'

dayjs.extend(relativeTime)

export type AssetListVariant = 'all' | 'my' | 'pending'

const renderDateCell = (value: unknown, variant: AssetListVariant) => {
    const date = dayjs(value as string)
    const display =
        variant === 'pending' ? date.format('DD/MM/YYYY') : date.fromNow()
    return (
        <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span
                className="text-sm"
                title={date.format('YYYY-MM-DD HH:mm:ss')}
            >
                {display}
            </span>
        </div>
    )
}

/**
 * Column definitions for Asset list, parameterized by variant
 */
export const getAssetColumns = (
    t: TFunction,
    options: { variant: AssetListVariant }
): DataTableColumn<AssetListItem>[] => {
    const { variant } = options

    const columns: DataTableColumn<AssetListItem>[] = [
        {
            key: 'asset_slug',
            title: t('fields.assetId'),
            width: 120,
            pinned: 'left',
            render: (value, row) => {
                const isDraft =
                    variant === 'my' && row.status?.toLowerCase() === 'draft'
                const to = isDraft
                    ? generatePathDatacentral(paths.assetEdit, {
                          id: row.asset_id,
                      })
                    : getAssetDetailPath(row, { includeVersion: false })

                return (
                    <Link
                        to={to}
                        className="uppercase text-blue-600 hover:underline"
                    >
                        {value}
                    </Link>
                )
            },
        },
        {
            key: 'asset_name',
            title: t('fields.assetName'),
            width: 200,
            render: (value) => (
                <div className="font-medium text-gray-900">{value}</div>
            ),
        },
        {
            key: 'document_type',
            title: t('fields.documentType'),
            width: 150,
            render: (_value, row) => {
                const type = row.current_version?.document_type
                return (
                    <Badge variant="outline">
                        {type ? t(`document_types.${type}`) : 'N/A'}
                    </Badge>
                )
            },
        },
        {
            key: 'department_name',
            title: t('fields.departmentId'),
            width: 170,
            render: (value) => (
                <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span className="text-xs">{value || 'N/A'}</span>
                </div>
            ),
        },
        {
            key: 'version',
            title: t('fields.version'),
            width: 120,
            render: (_value, row) => {
                const version = row.current_version
                const text = version
                    ? `${version.major}.${version.minor}.${version.patch}`
                    : 'N/A'
                return <Badge variant="outline">{text}</Badge>
            },
        },
    ]

    // total_files — skip for pending
    if (variant !== 'pending') {
        columns.push({
            key: 'total_files',
            title: t('tabs.attachments'),
            width: 100,
            render: (value) => (
                <div className="flex items-center justify-center gap-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{value || 0}</span>
                </div>
            ),
        })
    }

    // created_by_name — skip for my
    if (variant !== 'my') {
        columns.push({
            key: 'created_by_name',
            title: t('fields.createdBy'),
            width: 150,
            render: (value) => (
                <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{value || 'Unknown'}</span>
                </div>
            ),
        })
    }

    // created_at
    columns.push({
        key: 'created_at',
        title: t('fields.createdAt'),
        width: 150,
        render: (value) => renderDateCell(value, variant),
    })

    // updated_at
    columns.push({
        key: 'updated_at',
        title: t('fields.updatedAt'),
        width: 150,
        render: (value) => renderDateCell(value, variant),
    })

    // status
    columns.push({
        key: 'status',
        title: t('fields.status'),
        width: 120,
        render: (value) => (
            <AssetStatusBadge status={(value as string) || ''} />
        ),
    })

    return columns
}

/**
 * Row actions — eye icon to view detail
 */
export const getAssetActions = (
    t: TFunction,
    onView: (row: AssetListItem) => void
): DataTableAction<AssetListItem>[] => [
    {
        key: 'view',
        label: t('actions.viewDetails'),
        icon: <Eye className="w-4 h-4" />,
        onClick: onView,
    },
]

/**
 * Filter items configuration
 */
export const getAssetFilterItems = (
    t: TFunction,
    options?: { showStatus?: boolean }
): FilterItemConfig[] => {
    const documentGroupOptions = DOCUMENT_GROUPS.map((group) => ({
        label: t(`document_groups.${group.value}`),
        value: group.value,
    }))

    const documentTypeOptions = DOCUMENT_TYPES.map((type) => ({
        label: t(`document_types.${type.value}`),
        value: type.value,
    }))

    const items: FilterItemConfig[] = [
        {
            type: 'select',
            name: 'document_group',
            label: t('fields.documentGroup'),
            placeholder: t('filters.byCategory'),
            options: documentGroupOptions,
        },
        {
            type: 'department',
            name: 'department_id',
            label: t('fields.departmentId'),
        },
        {
            type: 'select',
            name: 'document_type',
            label: t('fields.documentType'),
            placeholder: t('filters.byDocumentType'),
            options: documentTypeOptions,
        },
    ]

    if (options?.showStatus) {
        items.push({
            type: 'select',
            name: 'status',
            label: t('fields.status'),
            placeholder: t('filters.byStatus'),
            options: [
                { label: t('asset_statuses.DRAFT'), value: 'DRAFT' },
                {
                    label: t('asset_statuses.PROCESSING'),
                    value: 'PROCESSING',
                },
                {
                    label: t('asset_statuses.PUBLISHED'),
                    value: 'PUBLISHED',
                },
                { label: t('asset_statuses.REJECTED'), value: 'REJECTED' },
                { label: t('asset_statuses.ARCHIVED'), value: 'ARCHIVED' },
            ],
        })
    }

    items.push({
        type: 'date-range',
        name: 'created_at',
        label: t('fields.createdAt'),
        cols: 2,
    })

    return items
}

/**
 * Filter toggles for column visibility
 */
export const getAssetFilterToggles = (
    t: TFunction,
    variant: AssetListVariant
): FilterToggle[] => {
    const toggles: FilterToggle[] = [
        { key: 'asset_slug', label: t('fields.assetId') },
        { key: 'asset_name', label: t('fields.assetName') },
        { key: 'document_type', label: t('fields.documentType') },
        { key: 'department_name', label: t('fields.departmentId') },
        { key: 'version', label: t('fields.version') },
    ]

    if (variant !== 'pending') {
        toggles.push({ key: 'total_files', label: t('tabs.attachments') })
    }

    if (variant !== 'my') {
        toggles.push({ key: 'created_by_name', label: t('fields.createdBy') })
    }

    toggles.push(
        { key: 'created_at', label: t('fields.createdAt') },
        { key: 'updated_at', label: t('fields.updatedAt') },
        { key: 'status', label: t('fields.status') }
    )

    return toggles
}
